import React, { useState, useCallback, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import {
  FileText, CheckCircle, AlertCircle, Upload,
  ArrowRight, Loader2, X, ChevronDown,
} from "lucide-react";
import { calculIR, TAUX_TOTAL_MICRO_CIPAV, TAUX_REVENU_IMPOSABLE_MICRO } from "@/utils/fiscal";

// ── Palette (cohérente avec l'app) ──────────────────────────────────────────
const C = {
  navy:      "#1B3A5C",
  navyDark:  "#0D2137",
  teal:      "#2A9D8F",
  lightTeal: "#E8F5F4",
  muted:     "#6B85A3",
  border:    "#D6E4F0",
  bg:        "#F4F8FC",
  white:     "#FFFFFF",
  danger:    "#E63946",
  warning:   "#F4A261",
};

const fmt = (n: number) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

// ── Types ────────────────────────────────────────────────────────────────────
type RawRow = Record<string, string>;

type MappedRow = {
  date: string;          // YYYY-MM-DD
  description: string;
  credit: number;        // recette
  debit: number;         // charge
};

type CategorizedRow = MappedRow & {
  category: string;
  ligne2035: string;
  isRecette: boolean;
  isCharge: boolean;
};

interface ColumnMapping {
  date:        string | null;
  description: string | null;
  credit:      string | null;
  debit:       string | null;
}

export interface ImportInsight {
  totalRecettes: number;
  totalCharges: number;
  nbRecettes: number;
}

export interface DataImporterProps {
  /** Callback pour persister les recettes importées */
  onImportRecettes: (rows: { date: string; description: string; montant: number }[]) => Promise<void>;
  onClose: () => void;
  /** Appelé avec les métriques dès l'import — permet au Dashboard d'afficher InsightFlash */
  onInsight?: (data: ImportInsight) => void;
  /** Tarif de séance (€) pour calculer les jours de vacances */
  tarifRef?: number;
}

// ── Parsers robustes ─────────────────────────────────────────────────────────

/** Gère 1 234,56 / 1234.56 / €1 234 / "" / "-" */
function parseAmount(s: string): number {
  if (!s) return 0;
  const clean = s
    .replace(/[€$£]/g, "")
    .replace(/[\s\u00a0\u202f]/g, "")
    .trim();
  if (!clean || clean === "-" || clean === "+") return 0;
  // Format FR : point = séparateur milliers, virgule = décimale
  const fr = clean.replace(/\.(?=\d{3}(?:[,]|$))/g, "").replace(",", ".");
  const n = parseFloat(fr);
  return isNaN(n) ? 0 : Math.abs(n);
}

/** Supporte DD/MM/YYYY · YYYY-MM-DD · DD-MM-YYYY · DD.MM.YYYY */
function parseDate(s: string): string {
  const t = (s || "").trim();
  const m1 = t.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
  if (m1) return `${m1[3]}-${m1[2].padStart(2, "0")}-${m1[1].padStart(2, "0")}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  return "";
}

// ── Détection auto des colonnes ───────────────────────────────────────────────
function detectColumns(headers: string[]): ColumnMapping {
  const norm = (s: string) =>
    s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
  const normed = headers.map(norm);

  const find = (...kws: string[]) => {
    for (const kw of kws) {
      const i = normed.findIndex(h => h.includes(kw));
      if (i >= 0) return headers[i];
    }
    return null;
  };

  const creditCol = find(
    "credit", "crédit", "recette", "recettes", "entree", "entrée", "avoir",
    "ttc credit", "montant credit", "credit eur", "montant encaisse",
    // Fichiers Indy / Abby / mono-colonne : "Montant" utilisé pour les recettes
    "montant ht", "montant ttc", "montant",
  );
  const debitCol = find(
    "debit", "débit", "depense", "dépense", "sortie",
    "charge", "montant debit", "debit eur",
  );
  // Si "Montant" est détecté comme credit ET qu'il n'y a pas de colonne débit,
  // cela correspond aux formats mono-colonne (Indy, Abby, exports perso).
  return {
    date:        find("date", "jour", "date operation", "date comptable"),
    description: find("libelle", "libellé", "description", "detail", "détail", "label",
                      "operation", "opération", "motif", "intitule", "reference", "nature"),
    credit:      creditCol,
    debit:       creditCol === debitCol ? null : debitCol,
  };
}

// ── Catégorisation "Smart Psy" ────────────────────────────────────────────────
const SMART_RULES: { keywords: string[]; category: string; ligne2035: string }[] = [
  { keywords: ["doctolib", "monpsy", "therapio", "stripe", "sumeria", "lemonway", "plateforme"],
    category: "Plateformes & frais de gestion", ligne2035: "21" },
  { keywords: ["loyer", "sci", "copropriete", "bail", "cabinet", "local pro"],
    category: "Local professionnel", ligne2035: "14" },
  { keywords: ["supervision", "intervision", "analyse didactique", "controleur"],
    category: "Supervision clinique", ligne2035: "30" },
  { keywords: ["abeille", "macsf", "allianz", "axa", "mma", "groupama",
               "prevoyance", "mutuelle", "rc pro", "assurance pro"],
    category: "Assurances pro", ligne2035: "22" },
  { keywords: ["formation", "dpc", "congres", "seminaire", "revue pro"],
    category: "Formations & documentation", ligne2035: "30" },
  { keywords: ["urssaf", "cipav", "cotisation sociale"],
    category: "Cotisations sociales", ligne2035: "25BT" },
  { keywords: ["expert comptable", "comptable", "avocat", "juriste"],
    category: "Honoraires professionnels", ligne2035: "21" },
  { keywords: ["orange pro", "sfr", "bouygues", "free pro", "telecom", "internet pro"],
    category: "Téléphone & internet", ligne2035: "27" },
  { keywords: ["sncf", "ratp", "navigo", "parking", "peage", "carburant", "transport pro"],
    category: "Déplacements", ligne2035: "24" },
  { keywords: ["fourniture", "papeterie", "papier", "imprimante", "encre"],
    category: "Fournitures de bureau", ligne2035: "27" },
  { keywords: ["madelin", "retraite complementaire", "perin", "perp"],
    category: "Retraite Madelin", ligne2035: "25bis" },
  { keywords: ["cfe", "cotisation fonciere"],
    category: "CFE", ligne2035: "autre" },
];

function categorize(label: string): { category: string; ligne2035: string } {
  const n = label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  for (const rule of SMART_RULES) {
    if (rule.keywords.some(kw => n.includes(kw))) {
      return { category: rule.category, ligne2035: rule.ligne2035 };
    }
  }
  return { category: "Autre", ligne2035: "autre" };
}

// ── Composant principal ───────────────────────────────────────────────────────
type Step = 1 | 2 | 3 | 4;

const LOADING_MSGS = [
  "Extraction des charges déductibles...",
  "Calcul de la limite de 83 600 €...",
  "Simulation de la réforme 2026...",
  "Identification des postes optimisables...",
  "Calcul de votre vrai salaire net...",
];

export default function DataImporter({ onImportRecettes, onClose, onInsight, tarifRef = 70 }: DataImporterProps) {
  const [step, setStep] = useState<Step>(1);
  const [fileError, setFileError] = useState("");
  const [headers, setHeaders] = useState<string[]>([]);
  const [previewRows, setPreviewRows] = useState<RawRow[]>([]);
  const [allRows, setAllRows] = useState<RawRow[]>([]);
  const [mapping, setMapping] = useState<ColumnMapping>({ date: null, description: null, credit: null, debit: null });
  const [categorized, setCategorized] = useState<CategorizedRow[]>([]);
  const [catProgress, setCatProgress] = useState(0);
  const [loadingMsgIdx, setLoadingMsgIdx] = useState(0);
  const [importing, setImporting] = useState(false);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const msgRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Step 1 : lecture du fichier ─────────────────────────────────────────────
  const processFile = useCallback((file: File) => {
    setFileError("");
    const ext = file.name.split(".").pop()?.toLowerCase();

    if (ext === "csv") {
      Papa.parse<RawRow>(file, {
        header: true,
        skipEmptyLines: true,
        encoding: "UTF-8",
        complete: ({ data, meta }) => {
          if (!data.length) { setFileError("Fichier CSV vide ou illisible."); return; }
          const hdrs = meta.fields ?? [];
          setHeaders(hdrs);
          setAllRows(data as RawRow[]);
          setPreviewRows(data.slice(0, 3) as RawRow[]);
          setMapping(detectColumns(hdrs));
          setStep(2);
        },
        error: () => setFileError("Impossible de lire ce fichier CSV. Vérifiez l'encodage (UTF-8)."),
      });
    } else if (ext === "xlsx" || ext === "xls") {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target?.result, { type: "binary", cellDates: true });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const data = XLSX.utils.sheet_to_json<RawRow>(ws, { defval: "", raw: false });
          if (!data.length) { setFileError("Feuille Excel vide."); return; }
          const hdrs = Object.keys(data[0]);
          setHeaders(hdrs);
          setAllRows(data);
          setPreviewRows(data.slice(0, 3));
          setMapping(detectColumns(hdrs));
          setStep(2);
        } catch {
          setFileError("Oups ! Ce fichier semble un peu capricieux. Vérifiez qu'il s'agit bien d'un format .csv ou .xlsx.");
        }
      };
      reader.readAsBinaryString(file);
    } else {
      setFileError("Oups ! Ce fichier semble un peu capricieux. Vérifiez qu'il s'agit bien d'un format .csv ou .xlsx.");
    }
  }, []);

  const onDrop = useCallback((accepted: File[], rejected: { file: File; errors: { code: string; message: string }[] }[]) => {
    if (rejected.length) {
      setFileError("Oups ! Ce fichier semble un peu capricieux. Vérifiez qu'il s'agit bien d'un format .csv ou .xlsx.");
      return;
    }
    if (accepted[0]) processFile(accepted[0]);
  }, [processFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "text/csv": [".csv"], "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"], "application/vnd.ms-excel": [".xls"] },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
  });

  // ── Step 3 : catégorisation animée ──────────────────────────────────────────
  const runCategorization = useCallback(() => {
    setStep(3);
    setCatProgress(0);

    const mapped: CategorizedRow[] = allRows
      .map(row => {
        if (!mapping.date || !mapping.description) return null;
        const date = parseDate(row[mapping.date] ?? "");
        if (!date) return null;
        const description = (row[mapping.description] ?? "").trim();
        const credit = mapping.credit ? parseAmount(row[mapping.credit] ?? "") : 0;
        const debit  = mapping.debit  ? parseAmount(row[mapping.debit]  ?? "") : 0;
        if (credit === 0 && debit === 0) return null;
        const { category, ligne2035 } = categorize(description);
        return {
          date, description, credit, debit, category, ligne2035,
          isRecette: credit > 0,
          isCharge:  debit > 0,
        } satisfies CategorizedRow;
      })
      .filter((r): r is CategorizedRow => r !== null);

    // Animation de progression
    let p = 0;
    setLoadingMsgIdx(0);
    progressRef.current = setInterval(() => {
      p += Math.random() * 12 + 4;
      if (p >= 100) {
        p = 100;
        clearInterval(progressRef.current!);
        if (msgRef.current) clearInterval(msgRef.current);
        setTimeout(() => { setCategorized(mapped); setStep(4); }, 400);
      }
      setCatProgress(Math.min(100, p));
    }, 80);

    // Cycling loading messages every 800ms
    msgRef.current = setInterval(() => {
      setLoadingMsgIdx(i => (i + 1) % LOADING_MSGS.length);
    }, 800);
  }, [allRows, mapping]);

  useEffect(() => () => {
    if (progressRef.current) clearInterval(progressRef.current);
    if (msgRef.current) clearInterval(msgRef.current);
  }, []);

  // ── Step 4 : import final ────────────────────────────────────────────────────
  const handleImport = useCallback(async () => {
    setImporting(true);
    const recettes = categorized
      .filter(r => r.isRecette)
      .map(r => ({ date: r.date, description: r.description, montant: r.credit }));
    // Recompute inside callback to avoid forward-reference in dep array
    const recettesRows = categorized.filter(r => r.isRecette);
    const snapRecettes = recettesRows.reduce((s, r) => s + r.credit, 0);
    const snapCharges  = categorized.filter(r => r.isCharge).reduce((s, r) => s + r.debit, 0);
    onInsight?.({ totalRecettes: snapRecettes, totalCharges: snapCharges, nbRecettes: recettesRows.length });
    await onImportRecettes(recettes);
    setImporting(false);
  }, [categorized, onImportRecettes, onInsight]);

  // ── Métriques résumé ─────────────────────────────────────────────────────────
  const totalRecettes = categorized.filter(r => r.isRecette).reduce((s, r) => s + r.credit, 0);
  const totalCharges  = categorized.filter(r => r.isCharge).reduce((s, r) => s + r.debit, 0);
  const nbRecettes    = categorized.filter(r => r.isRecette).length;
  const nbCharges     = categorized.filter(r => r.isCharge).length;

  const chargesByCategory = categorized
    .filter(r => r.isCharge)
    .reduce<Record<string, number>>((acc, r) => {
      acc[r.category] = (acc[r.category] ?? 0) + r.debit;
      return acc;
    }, {});

  // ── Sélecteur colonne ────────────────────────────────────────────────────────
  const ColSelect = ({ label, value, onChange }: {
    label: string; value: string | null; onChange: (v: string | null) => void;
  }) => (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-wide mb-1" style={{ color: C.muted }}>
        {label}
      </label>
      <div className="relative">
        <select
          value={value ?? ""}
          onChange={e => onChange(e.target.value || null)}
          className="w-full rounded-xl border px-3 py-2 text-xs appearance-none pr-7 outline-none"
          style={{ borderColor: value ? C.teal : C.border, backgroundColor: value ? C.lightTeal : C.white, color: C.navy }}
        >
          <option value="">— Non utilisée —</option>
          {headers.map(h => <option key={h} value={h}>{h}</option>)}
        </select>
        <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: C.muted }} />
      </div>
    </div>
  );

  // ── Rendu par étape ──────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Stepper */}
      <div className="flex items-center gap-1">
        {([1, 2, 3, 4] as Step[]).map((s, i) => {
          const labels = ["Fichier", "Colonnes", "Analyse", "Importer"];
          const done = step > s;
          const active = step === s;
          return (
            <React.Fragment key={s}>
              <div className="flex items-center gap-1">
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
                  style={{
                    backgroundColor: done ? C.teal : active ? C.navy : C.border,
                    color: done || active ? C.white : C.muted,
                  }}
                >
                  {done ? <CheckCircle size={11} /> : s}
                </div>
                <span className="text-[10px] font-semibold hidden sm:block" style={{ color: active ? C.navy : C.muted }}>
                  {labels[i]}
                </span>
              </div>
              {i < 3 && <div className="flex-1 h-px mx-1" style={{ backgroundColor: step > s ? C.teal : C.border }} />}
            </React.Fragment>
          );
        })}
      </div>

      {/* ── ÉTAPE 1 — Zone de dépôt ─────────────────────────────────────────── */}
      {step === 1 && (
        <div className="space-y-3">
          <div
            {...getRootProps()}
            className="rounded-2xl border-2 border-dashed p-10 flex flex-col items-center justify-center gap-4 cursor-pointer transition-all duration-200"
            style={{
              borderColor: isDragActive ? C.teal : `${C.teal}60`,
              backgroundColor: isDragActive ? `${C.navyDark}` : C.navyDark,
              minHeight: 220,
            }}
          >
            <input {...getInputProps()} />
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{ backgroundColor: `${C.teal}25`, border: `2px solid ${C.teal}60` }}
            >
              {isDragActive
                ? <Upload size={28} strokeWidth={1.5} style={{ color: C.teal }} />
                : <FileText size={28} strokeWidth={1.5} style={{ color: C.teal }} />
              }
            </div>
            <div className="text-center">
              <p className="font-bold text-base" style={{ color: C.white }}>
                {isDragActive ? "Relâchez pour analyser" : "Déposez votre export Excel, Indy ou Abby ici."}
              </p>
              <p className="text-sm mt-1" style={{ color: `${C.white}80` }}>
                Nous analysons vos données pour calculer votre vrai salaire net.
              </p>
            </div>
            <span
              className="text-xs font-bold px-5 py-2 rounded-full transition-opacity hover:opacity-80"
              style={{ backgroundColor: C.teal, color: C.white }}
            >
              Choisir un fichier
            </span>
            <p className="text-[10px]" style={{ color: `${C.white}50` }}>
              .csv · .xlsx · .xls — max 10 Mo
            </p>
          </div>

          {/* Bannière de réassurance sécuritaire */}
          <div className="flex items-center gap-2.5 rounded-xl px-4 py-3" style={{ backgroundColor: `${C.teal}12`, border: `1px solid ${C.teal}40` }}>
            <span className="text-base shrink-0">🔒</span>
            <p className="text-[11px] leading-relaxed" style={{ color: C.teal }}>
              <strong>Traitement sécurisé, conforme au secret professionnel des psychologues.</strong>{" "}
              Vos données ne quittent pas votre appareil — aucun fichier n'est envoyé sur un serveur.
              Vos données vous appartiennent.
            </p>
          </div>

          {fileError && (
            <div className="flex items-start gap-2 rounded-xl px-4 py-3 text-sm" style={{ backgroundColor: "#FEE8EA", color: C.danger }}>
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              {fileError}
            </div>
          )}

          {/* Formats supportés */}
          <div className="grid grid-cols-3 gap-2 text-center">
            {[
              { icon: "🏦", label: "CIC / LCL / BNP", sub: "Export CSV compte" },
              { icon: "📊", label: "Excel / Google Sheets", sub: "Tableau de bord perso" },
              { icon: "📱", label: "Indy / Abby / Pennylane", sub: "Export natif .csv" },
            ].map(({ icon, label, sub }) => (
              <div key={label} className="rounded-xl p-3" style={{ backgroundColor: C.bg, border: `1px solid ${C.border}` }}>
                <div className="text-xl mb-1">{icon}</div>
                <div className="text-[10px] font-bold" style={{ color: C.navy }}>{label}</div>
                <div className="text-[9px]" style={{ color: C.muted }}>{sub}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── ÉTAPE 2 — Mapping des colonnes ─────────────────────────────────── */}
      {step === 2 && (
        <div className="space-y-4">
          <div>
            <p className="text-sm font-bold" style={{ color: C.navy }}>Nous avons détecté {headers.length} colonnes.</p>
            <p className="text-xs mt-0.5" style={{ color: C.muted }}>Confirmez ou ajustez le rôle de chaque colonne :</p>
          </div>

          {/* Sélecteurs */}
          <div className="grid grid-cols-2 gap-3">
            <ColSelect label="📅 Date" value={mapping.date} onChange={v => setMapping(m => ({ ...m, date: v }))} />
            <ColSelect label="📝 Libellé / Description" value={mapping.description} onChange={v => setMapping(m => ({ ...m, description: v }))} />
            <ColSelect label="💚 Recettes (Crédit)" value={mapping.credit} onChange={v => setMapping(m => ({ ...m, credit: v }))} />
            <ColSelect label="🔴 Dépenses (Débit)" value={mapping.debit} onChange={v => setMapping(m => ({ ...m, debit: v }))} />
          </div>

          {/* Prévisualisation 3 premières lignes */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide mb-2" style={{ color: C.muted }}>
              Aperçu — 3 premières lignes
            </p>
            <div className="overflow-x-auto rounded-xl border" style={{ borderColor: C.border }}>
              <table className="w-full text-[10px]">
                <thead>
                  <tr style={{ backgroundColor: C.bg }}>
                    {[mapping.date, mapping.description, mapping.credit, mapping.debit]
                      .filter(Boolean)
                      .map(col => (
                        <th key={col} className="px-3 py-2 text-left font-bold whitespace-nowrap" style={{ color: C.navy }}>
                          {col}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: C.border }}>
                  {previewRows.map((row, i) => (
                    <tr key={i} style={{ backgroundColor: i % 2 ? C.bg : C.white }}>
                      {[mapping.date, mapping.description, mapping.credit, mapping.debit]
                        .filter(Boolean)
                        .map(col => (
                          <td key={col} className="px-3 py-2 max-w-[140px] truncate" style={{ color: C.navy }} title={row[col!]}>
                            {row[col!]}
                          </td>
                        ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[9px] mt-1" style={{ color: C.muted }}>{allRows.length} lignes détectées au total</p>
          </div>

          <div className="flex gap-3">
            <button onClick={() => setStep(1)} className="px-4 py-2 rounded-xl text-xs font-semibold border" style={{ borderColor: C.border, color: C.muted }}>
              ← Retour
            </button>
            <button
              onClick={runCategorization}
              disabled={!mapping.date || !mapping.description || (!mapping.credit && !mapping.debit)}
              className="flex-1 py-2.5 rounded-xl text-white text-sm font-bold transition-opacity hover:opacity-90 disabled:opacity-40 flex items-center justify-center gap-2"
              style={{ backgroundColor: C.teal }}
            >
              Analyser {allRows.length} transactions <ArrowRight size={15} />
            </button>
          </div>
        </div>
      )}

      {/* ── ÉTAPE 3 — Catégorisation animée ────────────────────────────────── */}
      {step === 3 && (
        <div className="flex flex-col items-center justify-center gap-6 py-10">
          <div
            className="w-20 h-20 rounded-2xl flex items-center justify-center"
            style={{ backgroundColor: `${C.teal}20` }}
          >
            <Loader2 size={36} strokeWidth={1.5} className="animate-spin" style={{ color: C.teal }} />
          </div>
          <div className="text-center space-y-1">
            <p className="font-bold text-base" style={{ color: C.navy }}>Intelligence Artificielle en cours d'analyse…</p>
            <p className="text-xs min-h-[1.25rem] transition-all duration-300" style={{ color: C.teal }}>
              {LOADING_MSGS[loadingMsgIdx]}
            </p>
          </div>
          <div className="w-full space-y-1">
            <div className="flex justify-between text-xs" style={{ color: C.muted }}>
              <span>Progression</span>
              <span>{Math.round(catProgress)}%</span>
            </div>
            <div className="w-full h-3 rounded-full overflow-hidden" style={{ backgroundColor: C.border }}>
              <div
                className="h-full rounded-full transition-all duration-100"
                style={{ width: `${catProgress}%`, backgroundColor: C.teal }}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            {["Supervision", "Loyer cabinet", "Assurances", "Formations", "CIPAV"].map(tag => (
              <span key={tag} className="text-[10px] px-2 py-1 rounded-full font-medium"
                style={{ backgroundColor: `${C.teal}15`, color: C.teal }}>
                {tag} ✓
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── ÉTAPE 4 — Résumé + Import ───────────────────────────────────────── */}
      {step === 4 && (
        <div className="space-y-4">
          {/* Bloc "Wow" */}
          <div className="rounded-2xl p-5 text-center" style={{ backgroundColor: C.navyDark }}>
            <div className="text-3xl mb-2">🎉</div>
            <p className="text-base font-extrabold" style={{ color: C.white }}>Analyse terminée</p>
            <p className="text-sm mt-1" style={{ color: `${C.white}80` }}>
              {nbRecettes + nbCharges} transactions détectées dans votre fichier
            </p>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="rounded-xl p-3" style={{ backgroundColor: `${C.teal}25` }}>
                <div className="text-xs font-semibold" style={{ color: C.teal }}>Total Recettes</div>
                <div className="text-xl font-extrabold mt-0.5" style={{ color: C.white }}>{fmt(totalRecettes)}</div>
                <div className="text-[10px]" style={{ color: `${C.white}60` }}>{nbRecettes} transactions</div>
              </div>
              <div className="rounded-xl p-3" style={{ backgroundColor: `${C.danger}20` }}>
                <div className="text-xs font-semibold" style={{ color: "#FF6B7A" }}>Total Charges</div>
                <div className="text-xl font-extrabold mt-0.5" style={{ color: C.white }}>{fmt(totalCharges)}</div>
                <div className="text-[10px]" style={{ color: `${C.white}60` }}>{nbCharges} transactions</div>
              </div>
            </div>
          </div>

          {/* ── Widget Gain Potentiel ─────────────────────────────────────── */}
          {totalRecettes > 0 && (() => {
            // Estimation revenu net micro-BNC — sources fiscal.ts (ARAPL 2025, CIPAV 2026)
            const cotisations = Math.round(totalRecettes * TAUX_TOTAL_MICRO_CIPAV);
            const imposable   = Math.round(totalRecettes * TAUX_REVENU_IMPOSABLE_MICRO);
            const ir          = calculIR(imposable);
            const netAnnuel = Math.max(0, totalRecettes - cotisations - ir);
            const jourValeur = tarifRef * 6;
            const joursLibres = jourValeur > 0 ? Math.floor(netAnnuel / jourValeur) : 0;
            return (
              <div className="rounded-2xl p-4 space-y-3" style={{ background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyDark} 100%)` }}>
                <div className="text-xs font-bold uppercase tracking-wide" style={{ color: C.teal }}>
                  🏖️ Votre Gain Potentiel
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px]" style={{ color: `${C.white}60` }}>Vrai salaire net estimé</div>
                    <div className="text-lg font-extrabold" style={{ color: C.white }}>{fmt(netAnnuel)}</div>
                    <div className="text-[10px]" style={{ color: `${C.white}50` }}>après CIPAV + IR</div>
                  </div>
                  <div>
                    <div className="text-[10px]" style={{ color: `${C.white}60` }}>En jours de liberté</div>
                    <div className="text-lg font-extrabold" style={{ color: C.teal }}>{joursLibres} jours</div>
                    <div className="text-[10px]" style={{ color: `${C.white}50` }}>à {tarifRef}€/séance × 6h</div>
                  </div>
                </div>
                <div className="flex items-start gap-2 rounded-xl px-3 py-2" style={{ backgroundColor: `${C.white}08` }}>
                  <span className="text-xs" style={{ color: `${C.white}70` }}>
                    Cotisations CIPAV : <strong style={{ color: C.white }}>{fmt(cotisations)}</strong>
                    {"  ·  "}IR estimé : <strong style={{ color: C.white }}>{fmt(ir)}</strong>
                  </span>
                </div>
              </div>
            );
          })()}

          {/* Charges par catégorie (info) */}
          {Object.keys(chargesByCategory).length > 0 && (
            <div className="rounded-xl border overflow-hidden" style={{ borderColor: C.border }}>
              <div className="px-4 py-2.5 text-xs font-bold" style={{ backgroundColor: C.bg, color: C.navy }}>
                📊 Dépenses catégorisées — à reporter dans Charges 2035
              </div>
              <div className="divide-y" style={{ borderColor: C.border }}>
                {Object.entries(chargesByCategory)
                  .sort(([, a], [, b]) => b - a)
                  .map(([cat, total]) => (
                    <div key={cat} className="flex items-center justify-between px-4 py-2">
                      <span className="text-xs" style={{ color: C.navy }}>{cat}</span>
                      <span className="text-xs font-bold" style={{ color: C.navy }}>{fmt(total)}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {nbCharges > 0 && (
            <div className="rounded-xl px-4 py-3 text-xs" style={{ backgroundColor: `${C.warning}15`, color: C.warning }}>
              ℹ️ Les dépenses sont affichées à titre d'information. Reportez-les manuellement dans l'onglet <strong>Charges 2035</strong> pour les déduire de votre bénéfice imposable.
            </div>
          )}

          {/* CTA */}
          <button
            onClick={handleImport}
            disabled={importing || nbRecettes === 0}
            className="w-full py-4 rounded-2xl text-white font-extrabold text-sm transition-all hover:opacity-90 disabled:opacity-40 flex items-center justify-center gap-2"
            style={{ backgroundColor: C.teal, fontSize: 15 }}
          >
            {importing ? (
              <><Loader2 size={16} className="animate-spin" /> Import en cours…</>
            ) : nbRecettes === 0 ? (
              "Aucune recette à importer"
            ) : (
              <><CheckCircle size={16} /> Calculer mon Net — Importer {nbRecettes} recettes ({fmt(totalRecettes)}) →</>
            )}
          </button>

          <button
            onClick={() => setStep(2)}
            className="w-full text-xs underline text-center"
            style={{ color: C.muted }}
          >
            ← Modifier le mapping
          </button>
        </div>
      )}
    </div>
  );
}
